/*
 * Gift with purchase (GWP)
 *
 * Two jobs, both driven by state Liquid computed (see snippets/gwp-offer.liquid):
 *
 *   chooseGift() - the customer picked a colour, so put that variant in the cart
 *                  as the gift (swapping out an earlier pick if there is one).
 *
 *   reconcile()  - remove the gift again if the cart no longer qualifies, drop a
 *                  line left over from a previous promotion, and clamp quantity
 *                  to 1. Deliberately never adds: choosing is the customer's.
 *
 * Every mutation re-renders the cart, which mounts this component afresh with an
 * updated desired state, so reconcile() settles as soon as there is nothing left
 * to fix.
 */
if (!window.Eurus.loadedScript.has('gwp.js')) {
  window.Eurus.loadedScript.add('gwp.js');

  /*
   * Shared by every instance on the page: the cart page renders one inside
   * main-cart-items and another inside the ajax-loaded cart drawer, so the
   * single-flight guard has to be global rather than per-component.
   */
  window.Eurus.gwp = window.Eurus.gwp || { busy: false, mutations: 0, blocked: false };

  /*
   * Backstop only. reconcile() never adds, so it cannot chase its own tail --
   * but a mutation that keeps failing would otherwise retry on every render.
   * Customer-initiated picks are not counted against it.
   */
  const MAX_MUTATIONS = 12;

  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      Alpine.data('xGwp', (config) => ({
        busy: false,
        pending: null,
        error: '',

        async reconcile() {
          const state = window.Eurus.gwp;
          if (state.blocked || state.busy) return;

          const action = this.nextAction(config);
          if (!action) return;

          if (state.mutations >= MAX_MUTATIONS) {
            state.blocked = true;
            return;
          }
          state.mutations += 1;

          try {
            await this.mutate(() => this.setGiftQuantity(action.lineKey, action.quantity));
          } catch (error) {
            state.blocked = true;
            console.error('[gwp]', error);
          }
        },

        /*
         * The single mutation needed to converge, or null when the cart is
         * already correct. Only ever one step per render.
         */
        nextAction({ qualifies, lineKey, lineQuantity, lineValid }) {
          if (!lineKey) return null;
          if (!lineValid) return { lineKey, quantity: 0 };
          if (!qualifies) return { lineKey, quantity: 0 };
          if (lineQuantity !== 1) return { lineKey, quantity: 1 };
          return null;
        },

        async chooseGift(variantId) {
          const state = window.Eurus.gwp;
          if (state.busy) return;

          this.pending = variantId;
          this.error = '';

          try {
            await this.mutate(async () => {
              /*
               * cart/change.js cannot swap a line's variant, so an earlier pick
               * is cleared first. Removing before adding also keeps the customer
               * from momentarily holding two gifts.
               */
              if (config.lineKey) {
                const cleared = await this.setGiftQuantity(config.lineKey, 0);
                if (cleared.status) throw new Error(cleared.description || cleared.message);
              }
              return this.addGift(variantId);
            });
          } catch (error) {
            this.error = window.Eurus.cart_error || 'Sorry, that gift could not be added.';
            console.error('[gwp]', error);
          } finally {
            this.pending = null;
          }
        },

        /*
         * Routes a cart write through the theme's own queue so it cannot race
         * other cart updates, then refreshes the cart UI from the response.
         */
        async mutate(run) {
          const state = window.Eurus.gwp;
          state.busy = true;
          this.busy = true;

          try {
            await Alpine.store('xCartHelper').waitForCartUpdate();
            window.updatingCart = true;

            const response = await run();

            /*
             * cart/add.js and cart/change.js answer 200 on success and carry a
             * `status` only on failure (422 out of stock, 404 unknown variant).
             */
            if (response.status) {
              throw new Error(response.description || response.message || 'Cart rejected the gift');
            }

            await Alpine.store('xCartHelper').reRenderSections(response.sections);

            const bubble = document.getElementById('cart-icon-bubble');
            if (bubble) {
              Alpine.store('xCartHelper').currentItemCount = parseInt(bubble.innerHTML);
            }
            document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
            return response;
          } finally {
            window.updatingCart = false;
            state.busy = false;
            this.busy = false;
          }
        },

        addGift(variantId) {
          return fetch(`${Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{ id: variantId, quantity: 1, properties: { _gwp: config.campaign } }],
              sections: Alpine.store('xCartHelper').getSectionsToRender().map((section) => section.id)
            })
          }).then((response) => response.json());
        },

        setGiftQuantity(lineKey, quantity) {
          return fetch(`${Shopify.routes.root}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: lineKey,
              quantity,
              sections: Alpine.store('xCartHelper').getSectionsToRender().map((section) => section.id)
            })
          }).then((response) => response.json());
        }
      }));
    });
  });
}
